import { useProxyStore } from 'bz-stores/hook/mobx-inject/useProxyStore';
import BaseStore from 'bz-stores/mobx/base-store';
import createContext from 'bz-utilities/hooks/context-v2';
import type { IEvent } from 'bz-utilities/modules/plugins/register/stores/mediator';
import { action, autorun, computed, observable, observe, reaction, runInAction } from 'mobx';
import type { IReactionDisposer, Lambda } from 'mobx';
import Moment from 'moment';
import { useEffect, useState } from 'react';
import Api from 'tm3-core/api';
import {
  AppointmentGetRequest,
  DiaryRequest,
  FilterClassesRequest,
  MonthlyScheduleRequest
} from 'tm3-core/api.dtos';
import type {
  AppointmentDetailsResponse,
  ClassSessionWithDetails,
  DiaryDefaultDataForUserResponse,
  GeneralPractitionerResponse,
  LocationOpeningHoursResponse,
  ResourceMode
} from 'tm3-core/api.dtos';
import { MonthlyScheduleResponse } from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import { ConfigObservable } from './config';
import DiaryCacheStore from './diary-cache-store';
import Resources from './diary-view-store/enums/resources';
import CalendarDisplay from './enums/calendar-display';
import ScheduleStore from './schedule-store';

export enum ETimeFormat {
  h12 = '12h',
  h24 = '24h'
}

export enum EMode {
  appointment = 'appointment',
  event = 'event',
  session = 'session'
}

export type TDiaryViewHours = {
  start: number;
  end: number;
};

export class DiaryViewStoreType {
  resources: number[] = [];
  @observable lsPrefix = '';
  @observable mode: EMode = EMode.appointment;

  @observable calendarDisplay: CalendarDisplay = CalendarDisplay.Day;
  @observable monthOverview: MonthlyScheduleResponse = new MonthlyScheduleResponse();
  @observable classes: observable.map<Date, ClassSessionWithDetails[]> = new Map();
  @observable hideDayWeekSelector = false;
  @observable timeFormat: ETimeFormat = ETimeFormat.h24;
  @observable privacy = false;
  @observable interval = 15;
  @observable resourceMode: Resources = Resources.practitioner;
  @observable cancellations = false;
  @observable location = -1;
  @observable rooms = observable.array<number>([-2]);
  @observable appointmentPractitioners = observable.array<number>([]);
  @observable classPractitioners = observable.array<number>([]);
  @observable classTypes = observable.array<number>([]);
  @observable courseTypes = observable.array<number>([]);
  @observable date: string = new Date().toJSON();
  @observable currentTime = new Date();
  @observable processing = true;
  @observable withoutSessions = true;
  @observable allLocations = true;
  @observable weekends = false;
  @observable compressed = true;
  @observable showCalendarSidebar = false;
  @observable pinCalendar = false;
  @observable locationPractitioners = {};
  @observable statsKeys = [];
  @observable loaded: boolean = false;
  @observable allOpeningHours: LocationOpeningHoursResponse[] = [];
  @observable isRestrictedDiaryView: boolean = false;

  //hack for appt selection
  @observable target = '';

  //hacked back prop to stop double fetch
  //investigate computed setters to solve this
  @observable _location: number | undefined = -1;

  @observable readonly _practitioners = observable.array<GeneralPractitionerResponse>([]);

  @computed get practitioners() {
    return this.resourceMode === Resources.classes
      ? this.classPractitioners
      : this.appointmentPractitioners;
  }

  @computed get isWeek(): boolean {
    return this.calendarDisplay === CalendarDisplay.Week;
  }

  @computed
  get currentLocationHours(): LocationOpeningHoursResponse[] {
    return this.allOpeningHours.filter((loh) => loh.locationId == this.location);
  }
  @computed
  get diaryViewOpeningHours(): TDiaryViewHours {
    if (!this.canRestrictDiaryView || !this.isRestrictedDiaryView) {
      return {
        end: 24,
        start: 0
      };
    }

    const startingHours = this.currentLocationHours.map((x) => {
      const m = Moment(x.start);
      return m.hours() + m.minutes() / 60;
    });
    const endingHours = this.currentLocationHours.map((x) => {
      const m = Moment(x.end);
      return m.hours() + m.minutes() / 60;
    });
    let end = Math.max(...endingHours) + this.diaryHourPadding;
    let start = Math.min(...startingHours) - this.diaryHourPadding;

    // Round start down to the nearest hour, end up to the nearest hour
    start = Math.floor(start);
    end = Math.ceil(end);

    // ensure these times are within 24h bounds
    return {
      end: end <= 24 ? end : 24,
      start: start > 0 ? start : 0
    };
  }

  onClearSelection?: () => void;

  private _disposers: (IReactionDisposer | Lambda)[] = [];
  private configDisposer?: Lambda;
  private lastFetchDate?: Moment.Moment;

  constructor() {
    //sync update with the minute
    let delay = (60 - new Date().getSeconds()) * 1000;
    setTimeout(() => {
      this.currentTime = new Date();
      setInterval(() => {
        this.currentTime = new Date();
      }, 60000);
    }, delay);
  }

  @action.bound
  async onLoad(userId?: number): Promise<void> {
    if (userId !== undefined && userId > 0) {
      this.lsPrefix = userId.toString();
    }

    const res = await BaseStore.defaultClient.post(new DiaryRequest({}));

    runInAction(() => {
      this._onLoad(res);
      this.loaded = true;
    });
  }

  async onReload(userId?: number): Promise<void> {
    //temp hack until ui refactor
    this._disposers.forEach((fn) => {
      fn();
    });
    await this.onLoad(userId);
  }

  @action.bound
  hideDayWeekSelection(): void {
    if (!this.allowAllPractitionerWeekView) {
      if (this.practitioners.length > 1) {
        this.hideDayWeekSelector = true;
      }
      if (this.practitioners.length === 1) {
        this.hideDayWeekSelector = false;
      }
    }
  }

  @action.bound
  verifyResources(locationId?: number) {
    let { allRooms, allPractitionerLocations } = DiaryCacheStore;
    const loc = locationId || this._location;

    let availRooms = allRooms.filter((x) => x.locationId === loc).map((x) => x.id);

    this.rooms.replace(this.getStorage(`roomLocations-${this.location}`, this.rooms));

    let orderedPractIds = allPractitionerLocations.filter((x) => x.locationId === loc);
    if (ConfigObservable.config.ALPHABETICAL_CACHED_LISTS != 'TRUE') {
      orderedPractIds = orderedPractIds.sort((a, b) => (a.diaryOrder ?? 0) - (b.diaryOrder ?? 0));
    }
    const orderedPractitionerIds = orderedPractIds.map((x) => x.practitionerId);

    let availPract = this.intersect(
      this._practitioners.map((x) => x.id),
      this.intersect(this.activePractitioners, orderedPractitionerIds)
    ).sort((a, b) => orderedPractitionerIds.indexOf(a) - orderedPractitionerIds.indexOf(b));

    const practitionerLocationsResult = this.getStorage(
      `practitionerLocations-${this.location}`,
      this.practitioners.slice()
    );
    const classPractitionerLocationsResult = this.getStorage(
      `classPractitionerLocations-${this.location}`,
      this.practitioners.slice()
    );

    const plr = Array.isArray(practitionerLocationsResult)
      ? practitionerLocationsResult
      : [practitionerLocationsResult];

    const clr = Array.isArray(classPractitionerLocationsResult)
      ? classPractitionerLocationsResult
      : [classPractitionerLocationsResult];

    let _classPract = availPract.length > 0 ? this.intersect(availPract, clr) : clr;
    let _pract = availPract.length > 0 ? this.intersect(availPract, plr) : plr;
    let _rooms = this.intersect(availRooms, this.rooms);
    if (_rooms.length === 0) {
      _rooms = [-2];
    }

    if (availPract.length > 0) {
      if (_pract.length === 0) _pract = [availPract[0]];
      if (_classPract.length === 0) _classPract = [availPract[0]];
    }

    this.rooms.replace(_rooms);
    this.appointmentPractitioners.replace(_pract);
    this.classPractitioners.replace(_classPract);

    if (loc != this._location) {
      this._location = locationId;
    }
  }

  @action
  _onLoad(diary: DiaryDefaultDataForUserResponse) {
    const { isClick } = process.env;
    const isPhone = !!window.mobileDetection.mobile();

    const defaultLocation = !!diary.currentLocation
      ? diary.currentLocation.id
      : this._location || 1;

    this.allOpeningHours = diary.locations.flatMap((x) => x.openingHours);

    this._location = this.getStorage('location', defaultLocation);
    this.isRestrictedDiaryView = this.getStorage('isRestrictedDiaryView', false);

    this._practitioners.replace(diary.practitioners);

    this._disposers.forEach((fn) => {
      fn();
    });
    const defaultToWeek = isClick && !isPhone;
    this._disposers = [
      this.applyStorage(
        'calendarDisplay',
        defaultToWeek ? CalendarDisplay.Week : CalendarDisplay.Day
      ),
      this.applyStorage('weekends', isClick),
      this.applyStorage('timeFormat', isClick ? '12h' : '24h'),
      this.applyStorage('date', new Date()),
      this.applyStorage('rooms', [diary.currentRoom.id]),
      this.applyStorage('privacy', false),
      this.applyStorage('interval', 15),
      this.applyStorage('location', defaultLocation),
      this.applyStorage('resourceMode', 'practitioner'),
      this.applyStorage(
        'appointmentPractitioners',
        diary.currentPractitioner ? [diary.currentPractitioner.id] : []
      ),
      this.applyStorage(
        'classPractitioners',
        diary.currentPractitioner ? [diary.currentPractitioner.id] : []
      ),
      this.applyStorage('classTypes', []),
      this.applyStorage('courseTypes', []),
      this.applyStorage('cancellations', true),
      this.applyStorage('withoutSessions', true),
      this.applyStorage('allLocations', true),
      this.applyStorage('isRestrictedDiaryView', this.isRestrictedDiaryView),
      this.applyStorage('showCalendarSidebar', false),
      autorun(this.onFetch.bind(this, false)),
      (this.configDisposer = observe(ConfigObservable.configMap, (change) => {
        if (change.name === 'ALLOWALLPRACTWEEKVIEW') {
          this.hideDayWeekSelection();
        }
      }))
    ];

    const doVerify = () => {
      runInAction(() => {
        this.verifyResources();
      });
      this._disposers.push(
        observe(this, 'location', (change) => {
          runInAction(() => {
            this.verifyResources(change.newValue);
          });
        })
      );
    };

    if (cache.practitionerLocations.loaded) doVerify();
    else
      this._disposers.push(
        observe(cache.practitionerLocations, 'loaded', () => {
          doVerify();
        })
      );
  }

  intersect<T>(available: T[], current: T[]): T[] {
    const all = new Set(available);
    const sub = new Set(current);
    return [...all].filter((x) => sub.has(x));
  }

  applyStorage(key: string, fallback: any) {
    this[key] = this.getStorage(key, fallback);

    return autorun(() => {
      let val: any = this[key];
      let result = !!val?.toJS ? val.toJS() : val;
      if (!!sessionStorage.getItem('diary-restore')) {
        return;
      }
      localStorage[this.lsPrefix + key] = JSON.stringify(result);
    });
  }

  getStorage(key: string, fallback: any) {
    var result = localStorage[this.lsPrefix + key];
    if (typeof result !== 'string' || result === '') {
      return fallback;
    }

    try {
      result = JSON.parse(result);
    } catch (err) {
      /*supress catch & fallback to string*/
    }

    return result;
  }

  @computed
  get getConfig(): Record<string, string> {
    const { config } = ConfigObservable;
    const diaryPrintState = localStorage.getItem('diaryPrintState');

    if (diaryPrintState !== null) {
      const lsConfig: Record<string, string> | null = JSON.parse(diaryPrintState);
      return lsConfig != null ? lsConfig : config;
    }

    return config;
  }

  @computed
  get diaryHourPadding(): number {
    if (!this.canRestrictDiaryView) {
      return 0;
    }

    return parseInt(this.getConfig['DIARY_HOURS_PADDING']) || 0;
  }

  @computed
  get canRestrictDiaryView(): boolean {
    return (this.getConfig['DIARY_HAS_OPENING_HOURS_VIEW'] || '').toLowerCase() === 'true';
  }

  @computed
  get activePractitioners(): number[] {
    return DiaryCacheStore.allPractitioners.filter((x) => x.status !== 0).map((x) => x.id);
  }

  @computed
  get workingWeekDays(): number {
    return parseInt(this.getConfig['WORKINGWEEKDAYS']);
  }

  @computed
  get allowAllPractitionerWeekView(): boolean {
    return (this.getConfig['ALLOWALLPRACTWEEKVIEW'] || '').toLowerCase() === 'y';
  }

  @computed
  get firstDayOfWeek(): number {
    return parseInt(this.getConfig['FIRSTDAYOFWEEK']);
  }

  @computed
  get todayLine(): boolean {
    return (this.getConfig['SHOWDIARYTODAYLINE'] || '').toLowerCase() === 'true';
  }

  @computed
  get showDoNextScreen(): boolean {
    return (this.getConfig['DONEXTENABLED'] || '').toLowerCase() === 'true';
  }

  @computed
  get multiDay(): boolean {
    return Math.log2(this.workingWeekDays) % 1 > 0;
  }

  @action.bound
  setDiarySessionMode(practitionerId: number, locationId: number, userId: number) {
    if (!this.loaded) {
      this.onLoad(userId);
    }
    let _cache = Object.assign({}, this);
    _cache._practitionerId = practitionerId;
    sessionStorage.setItem('diary-restore', JSON.stringify(_cache));

    this.practitioners.replace([practitionerId]);
    this._location = locationId;
    this.location = locationId;
    this.mode = EMode.session;
    this.interval = 60;
    this.withoutSessions = true;
    this.calendarDisplay = CalendarDisplay.Week;

    this.onFetch();
  }

  @action.bound
  restoreDiaryMode(): void {
    let json = sessionStorage.getItem('diary-restore');
    if (this.mode !== EMode.session) {
      return;
    }
    let restore = JSON.parse(json || '{}');
    restore.mode = 'appointment';

    sessionStorage.removeItem('diary-restore');

    Object.keys(restore).map((key) => {
      if (key.indexOf('_') === 0) {
        return;
      }
      this[key] = restore[key];
    });
  }

  filterClasses(classTypes: number[], courseTypes: number[], practitioners: number[]) {
    runInAction(() => {
      if (practitioners.length === 0) this.practitioners.clear();
      else this.practitioners.replace(practitioners);
      if (classTypes.length === 0) this.classTypes.clear();
      else this.classTypes.replace(classTypes);
      if (courseTypes.length === 0) this.courseTypes.clear();
      else this.courseTypes.replace(courseTypes);
      if (practitioners.length === 0) this.practitioners.clear();
      else this.practitioners.replace(practitioners);
    });
  }

  async onFetch(isAutoRefresh = false): void {
    let {
      date,
      isWeek,
      resourceMode,
      weekends,
      _location,
      withoutSessions,
      allLocations,
      classTypes,
      courseTypes,
      cancellations
    } = this;
    if (
      ![Resources.classes, Resources.room, Resources.practitioner, Resources.list].includes(
        resourceMode
      )
    )
      return;

    //allows fetch when config changes
    let firstDay = this.firstDayOfWeek;
    let workingDays = this.workingWeekDays;
    if (!firstDay || !workingDays) return;
    //-------------------------------
    this.processing = true;
    this.lastFetchDate = Moment();

    let resourceIds = resourceMode === 'room' ? this.rooms : this.practitioners;
    if (process.env.isClick) {
      if (resourceIds.length === 0) {
        resourceIds =
          this.activePractitioners.length > 0
            ? observable.array(this.activePractitioners)
            : observable.array([1]);
      }
    }

    if (resourceMode === Resources.classes) {
      const res = await BaseStore.defaultClient.post(
        new FilterClassesRequest({
          fromDate: date,
          classTypeIds: classTypes,
          courseTypeIds: courseTypes,
          locationIds: _location ? [_location] : [],
          practitionerIds: resourceIds,
          hideCancellations: cancellations,
          showWeekends: weekends,
          autoRefresh: isAutoRefresh
        })
      );
      this.classes.replace(res.result.weeklyClasses);
    } else if (this.calendarDisplay === CalendarDisplay.Month) {
      // only allow single practitioner for month view
      if (resourceIds.length > 1) resourceIds.replace(resourceIds.slice(0, 1));
      BaseStore.defaultClient
        .post(
          new MonthlyScheduleRequest({
            date: date,
            mode: resourceMode as unknown as ResourceMode,
            location: _location,
            resourceIds: resourceIds,
            autoRefresh: isAutoRefresh,
            showCancellations: cancellations
          })
        )
        .then(
          action((res) => {
            this.monthOverview = res.result;
            this.processing = false;
          })
        );
      return;
    }

    const request = {
      date: date,
      isWeek: isWeek,
      mode: resourceMode,
      location: _location,
      showWeekends: weekends,
      resourceIds: resourceIds,
      withoutSessions: withoutSessions,
      allLocations: allLocations
    };

    const queryString = !!isAutoRefresh ? '?autoRefresh=true' : '';
    Api.post('/api/diary/schedule' + queryString, request).then(
      action((res) => {
        this.statsKeys = res.body.result.keys;
        ScheduleStore.onLoad(res.body.result);
        cache.mediator.publish(new DiaryFetchEvent());
        this.processing = false;
      })
    );
  }

  @action.bound
  onUnload(): void {
    this.mode = EMode.appointment;
    this._disposers.forEach((fn) => {
      fn();
    });
  }

  @action.bound
  async _getAppt(apptId: number): Promise<AppointmentDetailsResponse> {
    const { result } = await BaseStore.defaultClient.get(new AppointmentGetRequest({ id: apptId }));

    return result;
  }

  @action.bound
  async moveToAppt(apptId: number): Promise<AppointmentDetailsResponse> {
    const { result } = await BaseStore.defaultClient.get(new AppointmentGetRequest({ id: apptId }));

    runInAction(() => {
      this.location = result.locationId;
      this.practitioners.replace([result.practitionerId]);
      this.date = Moment(result.startDateTime).toDate();
    });

    return result;
  }

  @action.bound
  protected _moveToAppt(appt: AppointmentDetailsResponse): void {
    this.location = appt.locationId;
    if (!this.practitioners.includes(appt.practitionerId)) {
      if (this.practitioners.length > 1) this.practitioners.push(appt.practitionerId);
      else this.practitioners.replace([appt.practitionerId]);
    }
    this.date = Moment(appt.startDateTime).toDate();
  }

  @action.bound
  async navigateToAppt(apptId: number): Promise<void> {
    this.target = `evt-appt-${apptId}`;

    const res = await this._getAppt(apptId);

    this._moveToAppt(res);
  }
}

export { default as Resources } from './diary-view-store/enums/resources';
const DiaryViewStore = new DiaryViewStoreType();
export default DiaryViewStore;

export class DiaryFetchEvent implements IEvent {
  public constructor(init?: Partial<DiaryFetchEvent>) {
    (Object as any).assign(this, init);
  }
}

export const useDiaryView = () => useProxyStore(DiaryViewStore);
